/**
 * The prompt for the password and authenticator code before a destructive
 * operation (TASK-649).
 *
 * A Lambda refuses a deploy, a password reset, a portal user change, a config
 * delete or a system-user password when the sign-in is more than an hour old.
 * The page then asks the person to confirm it is them, and sends the request
 * again. Several requests refused at once share one prompt.
 */

export class ReauthCancelled extends Error {
  constructor() {
    super('You did not confirm it is you, so the portal did not do it.');
    this.name = 'ReauthCancelled';
  }
}

/** The code step timed out (Cognito keeps it 3 minutes): the prompt goes back to the password. */
export class ReauthRestart extends Error {
  constructor() {
    super('The time to enter the code ran out. Enter your password again.');
    this.name = 'ReauthRestart';
  }
}

let pending: { promise: Promise<void>; resolve: () => void; reject: (error: Error) => void } | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Show the prompt, or join the one already showing. Settles when the person confirms or cancels. */
export function requestReauth(): Promise<void> {
  if (!pending) {
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    pending = { promise, resolve, reject };
    notify();
  }
  return pending.promise;
}

export function isReauthRequested(): boolean {
  return pending !== null;
}

/** The person confirmed: every waiting request goes again. */
export function finishReauth(): void {
  const done = pending;
  pending = null;
  done?.resolve();
  notify();
}

/** The person closed the prompt: every waiting request fails with a plain message. */
export function cancelReauth(): void {
  const done = pending;
  pending = null;
  done?.reject(new ReauthCancelled());
  notify();
}

export function onReauthChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
